import { createContext, useContext, useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../lib/firebase';
import { useAuth } from './AuthContext';
import { ref, onValue, set } from 'firebase/database';

const MembershipContext = createContext();

export function useMembership() {
  return useContext(MembershipContext);
}

export function MembershipProvider({ children }) {
  const { currentUser } = useAuth();
  const [membership, setMembership] = useState(null);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quotaLoading, setQuotaLoading] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setLoading(true);

      // Subscribe to user membership: users/{userId}/membership
      const membershipRef = ref(db, `users/${currentUser.uid}/membership`);
      const unsubscribe = onValue(membershipRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setMembership(data);
        } else {
          // Check if user is part of a team
          const userRef = ref(db, `users/${currentUser.uid}`);
          onValue(userRef, (userSnapshot) => {
            const userData = userSnapshot.val();
            if (userData?.teamId) {
              // User is part of a team, check team membership
              const teamMembershipRef = ref(db, `teams/${userData.teamId}/membership`);
              onValue(teamMembershipRef, (teamSnapshot) => {
                const teamMembership = teamSnapshot.val();
                if (teamMembership && (teamMembership.status === 'active' || teamMembership.status === 'trial')) {
                  setMembership({
                    ...teamMembership,
                    type: 'team',
                    teamId: userData.teamId
                  });
                } else {
                  setMembership(null);
                }
                setLoading(false);
              }, { onlyOnce: true });
            } else {
              setMembership(null);
              setLoading(false);
            }
          }, { onlyOnce: true });
        }
        setLoading(false);
      }, (error) => {
        console.error("Error fetching membership:", error);
        setMembership(null);
        setLoading(false);
      });

      return () => unsubscribe();
    } else {
      setMembership(null);
      setLoading(false);
    }
  }, [currentUser]);

  const enrollInMembership = async (tier, promoCode = null, trialDays = null) => {
    if (!currentUser) throw new Error("User not logged in.");
    setLoading(true);
    try {
      const updateMembershipFunction = httpsCallable(functions, 'update_user_membership');
      const result = await updateMembershipFunction({
        userId: currentUser.uid,
        action: 'enroll',
        tier: tier,
        promoCode: promoCode,
        trialDays: trialDays,
      });

      if (result.data.success) {
        setMembership(result.data.membership); // Update context with new membership

        // Write user profile data to new location: users/{user_ID}/profile
        try {
          const userProfileRef = ref(db, `users/${currentUser.uid}/profile`);
          const profileData = {
            displayName: currentUser.displayName || '',
            email: currentUser.email || '',
            lastUpdated: new Date().toISOString(),
            photoURL: currentUser.photoURL || '',
            uid: currentUser.uid
          };

          await set(userProfileRef, profileData);
          console.log('User profile data written successfully to new location');
        } catch (profileError) {
          console.error('Error writing user profile data:', profileError);
          // Don't throw error here as membership enrollment was successful
        }

        // Fetch quota info after enrollment
        await fetchQuotaInfo();

        return result.data;
      } else {
        throw new Error(result.data.error || 'Failed to enroll.');
      }
    } catch (error) {
      console.error("Error enrolling in membership:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotaInfo = async () => {
    if (!currentUser) return;

    setQuotaLoading(true);
    try {
      const getQuotaFunction = httpsCallable(functions, 'get_session_quota_info');
      const result = await getQuotaFunction();

      if (result.data.success) {
        if (result.data.hasQuota) {
          setQuotaInfo(result.data.quotaInfo);
        } else {
          setQuotaInfo(null); // No quota for paid/team tiers
        }
      }
    } catch (error) {
      console.error('Error fetching quota info:', error);
      setQuotaInfo(null);
    } finally {
      setQuotaLoading(false);
    }
  };

  const checkQuotaBeforeSession = async () => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Fetch latest quota info
    await fetchQuotaInfo();

    // If no quota info, user is on paid tier - allow creation
    if (!quotaInfo) {
      return { canCreate: true, reason: 'Paid tier - unlimited sessions' };
    }

    // Check if quota allows creation
    if (quotaInfo.remaining <= 0) {
      return {
        canCreate: false,
        reason: `Free tier quota exhausted (${quotaInfo.count}/${quotaInfo.limit} used). Please upgrade to create more sessions.`,
        quotaInfo
      };
    }

    return {
      canCreate: true,
      reason: `Free tier - ${quotaInfo.remaining} sessions remaining`,
      quotaInfo
    };
  };

  // Fetch quota info when membership changes or user logs in
  useEffect(() => {
    if (currentUser && membership) {
      // Only fetch quota for free tier users
      const tier = membership.tier || 'free';
      if (tier === 'free') {
        fetchQuotaInfo();
      } else {
        setQuotaInfo(null); // Clear quota for paid tiers
      }
    } else {
      setQuotaInfo(null);
    }
  }, [currentUser, membership]);

  const value = {
    membership,
    quotaInfo,
    loading,
    quotaLoading,
    enrollInMembership,
    fetchQuotaInfo,
    checkQuotaBeforeSession,
  };

  return (
    <MembershipContext.Provider value={value}>
      {children}
    </MembershipContext.Provider>
  );
} 